import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./ComprehensiveFinancialManagementPage.scss";
import heroBanner from "../../assets/images/banner-hands-with-chart.png";
import Copyright from "../../components/Copyright/Copyright";

const ComprehensiveFinancialManagementPage = () => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
            <div className="feature-page">
      {/* Hero Section */}
      <section className="feature-hero">
        <div className="container">
          <div className="feature-hero-image">
            <img src={heroBanner} alt="Comprehensive Financial Management Solution" />
          </div>
        </div>
      </section>

      {/* Comprehensive Financial Management Information */}
      <section className="feature-info">
        <div className="container">
          <div className="feature-grid">
            <div className="feature-details">
              <h2>Comprehensive financial management solution</h2>
              <p>
                CashManager provides a complete financial management platform for small to medium businesses
                working with accounting outsource services. Streamline your invoicing, payments, and reporting while
                maintaining seamless collaboration with your external accounting team.
              </p>

              <div className="feature-methods three-columns">
                <div className="feature-method">
                  <div className="method-content">
                    <h3>Standalone solution</h3>
                    <p>Complete financial management system that handles all your invoicing, payments, and reporting needs independently.</p>
                    <p className="integration-link">
                      <Link to="#" onClick={() => document.getElementById('feature-features').scrollIntoView({ behavior: 'smooth' })}>Learn more →</Link>
                    </p>
                  </div>
                </div>

                <div className="feature-method">
                  <div className="method-content">
                    <h3>Accounting outsource integration</h3>
                    <p>Seamless integration with your accounting outsource provider for streamlined financial data sharing and reporting.</p>
                    <p className="integration-link">
                      <Link to="/erp-crm-integration">Learn more →</Link>
                    </p>
                  </div>
                </div>

                <div className="feature-method">
                  <div className="method-content">
                    <h3>ERP system extension</h3>
                    <p>For larger businesses, CashManager will extend your existing ERP or CRM functionality with advanced financial capabilities.</p>
                    <p className="integration-link">
                      <Link to="/erp-crm-integration">Learn more →</Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="feature-features" id="feature-features">
              <h2>Key features</h2>

              <div className="features-list">
                <div className="feature-item">
                  <h3>Complete invoicing system</h3>
                  <p>Create, send, and track invoices for your customers with automated reminders.
                    {/* &nbsp;
                    <span className="integration-link">
                      <Link to="/electronic-invoicing">Learn about electronic invoicing →</Link>
                    </span> */}
                  </p>
                </div>

                <div className="feature-item">
                  <h3>Payment management</h3>
                  <p>Process incoming invoices, manage cash flow, and schedule recurring payments with bank integration.</p>
                </div>

                <div className="feature-item">
                  <h3>Financial reporting</h3>
                  <p>Generate comprehensive reports for your accounting outsource or internal use with real-time data.</p>
                </div>

                <div className="feature-item">
                  <h3>Multi-company support</h3>
                  <p>Manage multiple companies and entities from a single platform with consolidated reporting.</p>
                </div>

                <div className="feature-item">
                  <h3>User rights management</h3>
                  <p>Control access and permissions for different users including your accounting outsource team.</p>
                </div>

                <div className="feature-item">
                  <h3>Audit trail & compliance</h3>
                  <p>Maintain complete audit trails and ensure compliance with financial regulations and standards.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Business Types Section */}
      <section className="supported-section">
        <div className="container">
          <div className="section-header">
            <h2>Perfect for your business type</h2>
            <p>CashManager adapts to different business sizes and structures to meet your specific financial management needs.</p>
          </div>

          <div className="items-grid">
            <div className="item">
              <h3>Small businesses with accounting outsource</h3>
              <ul>
                <li>Complete invoicing and payment processing</li>
                <li>Reporting for your accounting firm</li>
                <li>Real-time financial data sharing</li>
                <li>Cost-effective solution for growing businesses</li>
                <li>Easy integration with accounting outsource workflows</li>
                <li>Reduced administrative burden</li>
              </ul>
            </div>

            <div className="item">
              <h3>Medium businesses with mixed approach</h3>
              <ul>
                <li>Standalone financial management capabilities</li>
                <li>Integration with existing accounting systems</li>
                <li>Advanced reporting and analytics</li>
                <li>Multi-user access and permissions</li>
                <li>Scalable solution for growing operations</li>
                <li>Flexible deployment options</li>
              </ul>
            </div>

            <div className="item">
              <h3>Large businesses with existing ERP/CRM</h3>
              <ul>
                <li>Extend existing system functionality</li>
                <li>Advanced financial processing capabilities</li>
                <li>Seamless data synchronization</li>
                <li>Custom integration solutions</li>
                <li>Enterprise-grade security and compliance</li>
                <li>Dedicated support and maintenance</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="implementation-process">
        <div className="container">
          <div className="section-header">
            <h2>Getting started</h2>
            <p>Simple steps to implement your comprehensive financial management solution</p>
          </div>

          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Business assessment</h3>
                <p>We analyze your current financial processes and determine the best CashManager configuration for your needs.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>System setup</h3>
                <p>Configure CashManager with your business data, user accounts, and integration requirements.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Integration & training</h3>
                <p>Set up integrations with your accounting outsource or existing systems and provide comprehensive training.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Go live & support</h3>
                <p>Launch your system with ongoing support and monitoring to ensure smooth operations.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to streamline your financial management?</h2>
            <p>Contact our team to discuss your business needs and find the perfect CashManager configuration for your company.</p>
            <Link to="/onboarding" className="btn btn-secondary btn-large">Get started</Link>
          </div>
        </div>
      </section>

      {/* Copyright Section */}
      <Copyright />
    </div>
  );
};

export default ComprehensiveFinancialManagementPage;
